import { useState, useEffect, useCallback, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { getProducts, getProduct } from '../../services/productsApi';

// Shared dialog for "Copy from another product" buttons on Dimensions and
// Pricing cards. The picker autocompletes against the user's existing
// products; on Apply it fetches the full source product and hands the
// caller the field they asked for (extractField). The caller then chooses
// how to apply it to local state — usually a setter that overwrites the
// current section's value. Save is still required afterwards.
const CopyFromProductDialog = ({
  open,
  onClose,
  title,
  excludeProductId,
  extractField,   // (sourceProduct) => the value to hand back
  onApply,        // (value, sourceProduct) => void
  helperText,
}) => {
  const [products, setProducts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [fetchingSource, setFetchingSource] = useState(false);
  const [error, setError] = useState('');

  // Search the FULL catalog server-side (the backend caps a page at 100, so we
  // can't just load everything). Typing refetches with the search term.
  const loadList = useCallback(async (search = '') => {
    setLoadingList(true);
    setError('');
    try {
      const data = await getProducts(1, 50, search ? { search } : {});
      const list = (data.products || []).filter((p) => p.id !== excludeProductId);
      setProducts(list);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load products');
    } finally {
      setLoadingList(false);
    }
  }, [excludeProductId]);

  const searchTimer = useRef(null);
  const handleInputChange = (_, value, reason) => {
    if (reason === 'reset') return; // fired when an option is selected
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => loadList((value || '').trim()), 300);
  };

  useEffect(() => {
    if (open) {
      setSelected(null);
      setError('');
      loadList();
    }
  }, [open, loadList]);

  const handleApply = async () => {
    if (!selected) return;
    setFetchingSource(true);
    setError('');
    try {
      const data = await getProduct(selected.id);
      const value = extractField(data.product);
      onApply(value, data.product);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load source product');
    } finally {
      setFetchingSource(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {helperText || 'Pick a source product. Its values will overwrite the current section. You still need to Save afterwards.'}
        </Typography>
        <Autocomplete
          size="small"
          options={products}
          loading={loadingList}
          value={selected}
          onChange={(_, val) => setSelected(val)}
          onInputChange={handleInputChange}
          filterOptions={(x) => x}
          getOptionLabel={(o) => (o ? `${o.title}${o.sku ? ` — ${o.sku}` : ''}` : '')}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Source product"
              placeholder="Type to search…"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingList ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          fullWidth
        />
        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={fetchingSource}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!selected || fetchingSource}
        >
          {fetchingSource ? <CircularProgress size={18} color="inherit" /> : 'Replace'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyFromProductDialog;
